<template>
  <span :class="badgeClasses">
    <!-- Icon (left) -->
    <span v-if="iconLeft" class="mr-1">
      <slot name="icon-left">{{ iconLeft }}</slot>
    </span>

    <!-- Badge content -->
    <slot>{{ text }}</slot>

    <!-- Icon (right) -->
    <span v-if="iconRight" class="ml-1">
      <slot name="icon-right">{{ iconRight }}</slot>
    </span>

    <!-- Close button -->
    <button
      v-if="closable"
      type="button"
      class="ml-1 hover:opacity-70 focus:outline-none"
      @click="handleClose"
    >
      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </button>
  </span>
</template>

<script>
export default {
  name: 'BaseBadge',
  props: {
    text: {
      type: [String, Number],
      default: null
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        'primary',
        'secondary',
        'success',
        'danger',
        'warning',
        'info',
        'gray',
        'dark'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value)
    },
    rounded: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'full'].includes(value)
    },
    outline: {
      type: Boolean,
      default: false
    },
    dot: {
      type: Boolean,
      default: false
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    closable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    badgeClasses() {
      return [
        'inline-flex items-center font-medium transition-colors duration-200',
        this.sizeClasses,
        this.variantClasses,
        this.roundedClasses,
        this.dot ? 'pl-1' : ''
      ]
    },
    sizeClasses() {
      const sizes = {
        xs: 'px-2 py-0.5 text-xs',
        sm: 'px-2.5 py-0.5 text-xs',
        md: 'px-3 py-1 text-sm',
        lg: 'px-4 py-1.5 text-base'
      }
      return sizes[this.size]
    },
    variantClasses() {
      if (this.outline) {
        const outlineVariants = {
          primary: 'bg-transparent text-primary border border-primary',
          secondary: 'bg-transparent text-secondary border border-secondary',
          success: 'bg-transparent text-success border border-success',
          danger: 'bg-transparent text-danger border border-danger',
          warning: 'bg-transparent text-warning border border-warning',
          info: 'bg-transparent text-info border border-info',
          gray: 'bg-transparent text-gray-700 border border-gray-300',
          dark: 'bg-transparent text-gray-900 border border-gray-900'
        }
        return outlineVariants[this.variant]
      }

      const variants = {
        primary: 'bg-primary text-white',
        secondary: 'bg-secondary text-white',
        success: 'bg-success text-white',
        danger: 'bg-danger text-white',
        warning: 'bg-warning text-gray-900',
        info: 'bg-info text-white',
        gray: 'bg-gray-200 text-gray-800',
        dark: 'bg-gray-800 text-white'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      const rounded = {
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      }
      return rounded[this.rounded]
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>
